import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import Table from "../../core/pagination/datatable";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import CommonFooter from "../../core/common/footer/commonFooter";
import {
  downloadPurchaseOrderReportExcel,
  downloadPurchaseOrderReportPdf,
  getPurchaseOrderReport,
  mapPurchaseOrderReportRow,
} from "../../api/purchaseOrderReportApi";

const DEFAULT_PRODUCT_IMAGE = `${process.env.PUBLIC_URL || ""}/assets/img/by-default.png`;

const IMAGE_BASE = (
  process.env.REACT_APP_API_IMAGE_BASE_URL ||
  "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

const normalizeKey = (v = "") =>
  typeof v === "string" ? v.replace(/^\/+/, "") : "";

const isAbsoluteHttp = (url = "") => /^https?:\/\//i.test(url);
const isDataUrl = (url = "") => /^data:image/i.test(url);

const resolveImageUrl = (raw) => {
  if (!raw) return DEFAULT_PRODUCT_IMAGE;

  const baseVal = Array.isArray(raw) ? raw[0] : raw;
  const val =
    typeof baseVal === "object" && baseVal !== null
      ? baseVal.url ||
        baseVal.imageUrl ||
        baseVal.key ||
        baseVal.path ||
        baseVal.location ||
        baseVal.src ||
        ""
      : baseVal;

  if (!val || typeof val !== "string") return DEFAULT_PRODUCT_IMAGE;
  if (isDataUrl(val)) return val;
  if (isAbsoluteHttp(val)) return val;

  const key = normalizeKey(val);
  if (!key) return DEFAULT_PRODUCT_IMAGE;
  return `${IMAGE_BASE}/${key}`;
};

const PurchaseReport = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    totalProducts: 0,
    totalPurchaseAmount: 0,
    totalPurchaseQty: 0,
    totalInstockQty: 0,
  });

  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    product: null,
    store: null,
    date: null,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    product: null,
    store: null,
    date: null,
  });

  const [toast, setToast] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, type, message });
    window.clearTimeout(window.__purchaseReportToastTimer);
    window.__purchaseReportToastTimer = window.setTimeout(() => {
      setToast({ show: false, type: "success", message: "" });
    }, 2500);
  };

  const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

  const fetchReport = async (params = {}) => {
    try {
      setLoading(true);

      const response = await getPurchaseOrderReport({
        page: 1,
        limit: 100,
        ...params,
      });

      const tableRows = Array.isArray(response?.rows)
        ? response.rows.map((item) => mapPurchaseOrderReportRow(item))
        : [];

      setRows(tableRows);
      setSummary(
        response?.summary || {
          totalProducts: 0,
          totalPurchaseAmount: 0,
          totalPurchaseQty: 0,
          totalInstockQty: 0,
        }
      );
    } catch (error) {
      console.error(error);
      setRows([]);
      setSummary({
        totalProducts: 0,
        totalPurchaseAmount: 0,
        totalPurchaseQty: 0,
        totalInstockQty: 0,
      });
      showToast(
        error?.message || window.translate("Failed to fetch purchase report"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const storeOptions = useMemo(() => {
    const uniqueStores = [];
    const seen = new Set();

    rows.forEach((row) => {
      const label = row?.raw?.warehouseName || row?.raw?.storeName || "";
      const value = row?.raw?.warehouse || row?.raw?.store || "";

      if (label && value && !seen.has(String(value))) {
        seen.add(String(value));
        uniqueStores.push({
          value: String(value),
          label,
        });
      }
    });

    return uniqueStores;
  }, [rows]);

  const productOptions = useMemo(() => {
    return rows.map((row) => ({
      value: row.productId,
      label: row.productName,
    }));
  }, [rows]);

  const buildQueryParams = () => {
    const params = {};

    if (filters.search?.trim()) {
      params.search = filters.search.trim();
    }

    if (filters.product?.value) {
      params.search = filters.product.label;
    }

    if (filters.store?.value) {
      params.warehouse = filters.store.value;
    }

    if (filters.date) {
      const selectedDate = dayjs(filters.date);
      params.startDate = selectedDate.format("YYYY-MM-DD");
      params.endDate = selectedDate.format("YYYY-MM-DD");
    }

    return params;
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    const params = buildQueryParams();
    setAppliedFilters({
      ...filters,
    });
    await fetchReport(params);
  };

  const handleRefresh = async () => {
    const params = {};

    if (appliedFilters.search?.trim()) {
      params.search = appliedFilters.search.trim();
    }

    if (appliedFilters.product?.value) {
      params.search = appliedFilters.product.label;
    }

    if (appliedFilters.store?.value) {
      params.warehouse = appliedFilters.store.value;
    }

    if (appliedFilters.date) {
      const selectedDate = dayjs(appliedFilters.date);
      params.startDate = selectedDate.format("YYYY-MM-DD");
      params.endDate = selectedDate.format("YYYY-MM-DD");
    }

    await fetchReport(params);
  };

  const handleReset = async () => {
    const empty = {
      search: "",
      product: null,
      store: null,
      date: null,
    };
    setFilters(empty);
    setAppliedFilters(empty);
    await fetchReport();
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);

      const params = {};
      if (appliedFilters.search?.trim()) params.search = appliedFilters.search.trim();
      if (appliedFilters.product?.value) params.search = appliedFilters.product.label;
      if (appliedFilters.store?.value) params.warehouse = appliedFilters.store.value;
      if (appliedFilters.date) {
        const selectedDate = dayjs(appliedFilters.date);
        params.startDate = selectedDate.format("YYYY-MM-DD");
        params.endDate = selectedDate.format("YYYY-MM-DD");
      }

      await downloadPurchaseOrderReportExcel(params);
      showToast(window.translate("Excel downloaded successfully"), "success");
    } catch (error) {
      console.error(error);
      showToast(
        error?.message || window.translate("Failed to download excel"),
        "error"
      );
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);

      const params = {};
      if (appliedFilters.search?.trim()) params.search = appliedFilters.search.trim();
      if (appliedFilters.product?.value) params.search = appliedFilters.product.label;
      if (appliedFilters.store?.value) params.warehouse = appliedFilters.store.value;
      if (appliedFilters.date) {
        const selectedDate = dayjs(appliedFilters.date);
        params.startDate = selectedDate.format("YYYY-MM-DD");
        params.endDate = selectedDate.format("YYYY-MM-DD");
      }

      await downloadPurchaseOrderReportPdf(params);
      showToast(window.translate("PDF downloaded successfully"), "success");
    } catch (error) {
      console.error(error);
      showToast(
        error?.message || window.translate("Failed to download pdf"),
        "error"
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  const summaryCards = [
    {
      title: window.translate("Total Products"),
      value: Number(summary?.totalProducts || 0),
      icon: "ti ti-box",
      bgClass: "bg-primary-transparent",
      textClass: "text-primary",
    },
    {
      title: window.translate("Total Purchase Amount"),
      value: formatCurrency(summary?.totalPurchaseAmount || 0),
      icon: "ti ti-currency-dollar",
      bgClass: "bg-success-transparent",
      textClass: "text-success",
    },
    {
      title: window.translate("Total Purchase Qty"),
      value: Number(summary?.totalPurchaseQty || 0),
      icon: "ti ti-shopping-cart",
      bgClass: "bg-warning-transparent",
      textClass: "text-warning",
    },
    {
      title: window.translate("Total Instock Qty"),
      value: Number(summary?.totalInstockQty || 0),
      icon: "ti ti-stack-2",
      bgClass: "bg-info-transparent",
      textClass: "text-info",
    },
  ];

  const columns = [
    {
      title: window.translate("Product Name"),
      dataIndex: "productName",
      render: (text, record) => {
        const imageSrc = resolveImageUrl(
          record?.img ||
            record?.raw?.imageUrl ||
            record?.raw?.image ||
            record?.raw?.productImage
        );

        return (
          <span className="productimgname">
            <Link to="#" className="product-img stock-img">
              <img
                src={imageSrc}
                alt={text || window.translate("product")}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                }}
              />
            </Link>
            <div>
              <Link to="#">{text}</Link>
              {record?.sku || record?.itemCode ? (
                <div className="fs-12 text-muted">
                  {record?.sku
                    ? `${window.translate("SKU")}: ${record.sku}`
                    : ""}
                  {record?.sku && record?.itemCode ? " | " : ""}
                  {record?.itemCode
                    ? `${window.translate("Code")}: ${record.itemCode}`
                    : ""}
                </div>
              ) : null}
            </div>
          </span>
        );
      },
      sorter: (a, b) => (a.productName || "").localeCompare(b.productName || ""),
    },
    {
      title: window.translate("Product Amount"),
      dataIndex: "productAmountLabel",
      render: (_, record) => (
        <span className="fw-semibold">{formatCurrency(record.productAmount)}</span>
      ),
      sorter: (a, b) => Number(a.productAmount || 0) - Number(b.productAmount || 0),
    },
    {
      title: window.translate("Product Qty"),
      dataIndex: "productQty",
      render: (text) => Number(text || 0),
      sorter: (a, b) => Number(a.productQty || 0) - Number(b.productQty || 0),
    },
    {
      title: window.translate("Instock Qty"),
      dataIndex: "instockQty",
      render: (text, record) => (
        <span
          className={`badge ${
            Number(record?.instockQty || 0) > 0
              ? "bg-success-transparent text-success"
              : "bg-danger-transparent text-danger"
          }`}
        >
          {Number(text || 0)}
        </span>
      ),
      sorter: (a, b) => Number(a.instockQty || 0) - Number(b.instockQty || 0),
    },
  ];

  return (
    <div className="page-wrapper">
      {toast.show ? (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 99999,
            minWidth: "280px",
            maxWidth: "420px",
            background: toast.type === "success" ? "#28c76f" : "#ea5455",
            color: "#fff",
            padding: "14px 16px",
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            fontWeight: 500,
          }}
        >
          <div className="d-flex justify-content-between align-items-center gap-3">
            <span>{toast.message}</span>
            <button
              type="button"
              onClick={() =>
                setToast({ show: false, type: "success", message: "" })
              }
              style={{
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: "18px",
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        </div>
      ) : null}

      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>{window.translate("Purchase Report")}</h4>
              <h6>{window.translate("Manage your Purchase report")}</h6>
            </div>
          </div>

          <ul className="table-top-head">
            <li>
              <Link to="#" onClick={handleRefresh}>
                <RefreshIcon />
              </Link>
            </li>
            <CollapesIcon />
          </ul>
        </div>

        <div className="row">
          {summaryCards.map((card) => (
            <div className="col-xl-3 col-md-6 col-sm-12 d-flex" key={card.title}>
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="mb-1 text-muted">{card.title}</p>
                      <h4 className="mb-0">{card.value}</h4>
                    </div>
                    <span className={`avatar avatar-lg rounded-circle ${card.bgClass}`}>
                      <i className={`${card.icon} ${card.textClass} fs-20`} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card border-0">
          <div className="card-body pb-1">
            <form onSubmit={handleGenerateReport}>
              <div className="row align-items-end">
                <div className="col-lg-10">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Choose Date")}
                        </label>
                        <div className="input-icon-start position-relative">
                          <DatePicker
                            value={filters.date}
                            onChange={(date) =>
                              setFilters((prev) => ({ ...prev, date }))
                            }
                            className="form-control datetimepicker"
                            placeholder={window.translate("dd/mm/yyyy")}
                          />
                          <span className="input-icon-left">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Store")}
                        </label>
                        <Select
                          classNamePrefix="react-select"
                          options={storeOptions}
                          value={filters.store}
                          onChange={(selected) =>
                            setFilters((prev) => ({ ...prev, store: selected }))
                          }
                          placeholder={window.translate("Choose")}
                          isClearable
                        />
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Products")}
                        </label>
                        <Select
                          classNamePrefix="react-select"
                          options={productOptions}
                          value={filters.product}
                          onChange={(selected) =>
                            setFilters((prev) => ({ ...prev, product: selected }))
                          }
                          placeholder={window.translate("Choose")}
                          isClearable
                        />
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Search")}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={window.translate("Search by name, sku...")}
                          value={filters.search}
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              search: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-2">
                  <div className="mb-3">
                    <button className="btn btn-primary w-100" type="submit">
                      {loading
                        ? window.translate("Generating...")
                        : window.translate("Generate Report")}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <div className="d-flex gap-2 flex-wrap pb-3">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleDownloadExcel}
                disabled={downloadingExcel}
              >
                <i className="ti ti-file-spreadsheet me-3 ms-3" />
                {downloadingExcel
                  ? window.translate("Downloading...")
                  : window.translate("Download Excel")}
              </button>

              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
              >
                <i className="ti ti-file-type-pdf me-3 ms-3" />
                {downloadingPdf
                  ? window.translate("Downloading...")
                  : window.translate("Download PDF")}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReset}
              >
                <i className="ti ti-rotate me-3 ms-3" />
                {window.translate("Reset")}
              </button>
            </div>
          </div>
        </div>

        <div className="card table-list-card hide-search">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div>
              <h4>{window.translate("Purchase Report")}</h4>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <li>
                <Link to="#" onClick={handleDownloadPdf}>
                  <i className="ti ti-printer" />
                </Link>
              </li>
            </ul>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={rows} loading={loading} />
            </div>
          </div>
        </div>
      </div>

      <CommonFooter />
    </div>
  );
};

export default PurchaseReport;